import React, { useState } from "react";

import {
  SpeakerPhone as SpeakerPhoneIcon,
  Info as InfoIcon,
  PersonOffOutlined as PersonOffOutlinedIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  DeviceThermostat as DeviceThermostatIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
} from "@mui/icons-material";
import {
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { getInitials } from "utils/string";
import { formatLocalDateTime } from "utils/date";
import { useStyles } from "components/pages/transmissions/modal/detail/alert/deactivatedPatientBannerStyle";

const AlertBannerForDeactivatePatient = ({ data }) => {
  const theme = useTheme();
  const classes = useStyles();

  const [isExpanded, setIsExpanded] = useState(false);

  const alertCount = [
    data?.deactivatedInfo,
    data?.preferredLanguage,
    data?.deviceAlertText?.length > 0,
    data?.leadAlertText?.length > 0,
    data?.bannerText ||
      data?.subscriptionPausedText ||
      data?.subscriptionRemoveText ||
      data?.monthlyBatteryCheckData ||
      data?.inOfficeOnlyText,
  ].filter(Boolean).length;

  const hasAdditionalAlerts = alertCount > (data?.deactivatedInfo ? 1 : 0);

  return (
    <Accordion
      expanded={isExpanded}
      onChange={() => hasAdditionalAlerts && setIsExpanded(!isExpanded)}
      sx={{
        margin: 0,
        backgroundColor: theme.palette.type === "dark" ? "#4B371A" : "#fff4e5",
        border: "1px solid",
        borderColor:
          theme.palette.type === "dark"
            ? "rgba(255, 193, 7, 0.5)"
            : "rgba(255, 152, 0, 0.5)",
        boxShadow: "none",
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        sx={{
          padding: "12px 16px",
          paddingBottom: isExpanded ? 0 : "12px",
          minHeight: "auto",
          boxShadow: "none",
          "& .MuiAccordionSummary-content": {
            margin: 0,
            display: "flex",
            flexDirection: "column",
            width: "100%",
          },
        }}
      >
        <div className={classes.headerContainer}>
          <div className={classes.leftSection}>
            <span className={`d-flex align-items-center ${classes.alertTitle}`}>
              <InfoIcon />
              Patient Alerts ({alertCount})
            </span>
            {data?.deactivatedInfo && (
              <span
                className={`dialog-content-alert-text ${classes.alertText} ${classes.deactivatedInfo}`}
              >
                <PersonOffOutlinedIcon />
                <span key="deactivatedInfo">
                  Patient Inactive:
                  {data?.deactivatedInfo?.reason &&
                    ` ${data.deactivatedInfo?.reason}`}
                  {data?.deactivatedInfo?.updated_at &&
                    ` (Deactivated on ${formatLocalDateTime(data.deactivatedInfo?.updated_at)}`}
                  {data.deactivatedInfo?.updated_by &&
                    ` by ${getInitials(data.deactivatedInfo.updated_by?.name, " ")})`}
                </span>
              </span>
            )}
          </div>
          {hasAdditionalAlerts && (
            <div className={classes.showMoreContainer}>
              <span className={classes.showMoreText}>
                {isExpanded ? "Show Less" : "Show More"}
              </span>
              <ExpandMoreIcon
                sx={{
                  fontSize: "20px",
                  color: "#A0650E",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </div>
          )}
        </div>
      </AccordionSummary>

      {hasAdditionalAlerts && (
        <AccordionDetails className={classes.accordionDetails}>
          <div className={classes.detailsContainer}>
            <span
              className={`d-flex flex-column dialog-content-alert-text ${classes.alertText}`}
            >
              {data?.preferredLanguage && (
                <span key="preferredLanguage">
                  <RecordVoiceOverIcon
                    className={`${classes.iconSpacing} ml-1 mr-2`}
                  />
                  Patient speaks {data.preferredLanguage}
                </span>
              )}
              {data?.deviceAlertText?.length > 0 && (
                <span key="deviceAlertText">
                  <SpeakerPhoneIcon className={`${classes.iconSpacing} mr-3`} />
                  {data?.deviceAlertText}
                </span>
              )}
              {data?.leadAlertText?.length > 0 && (
                <span key="leadAlertText">
                  <DeviceThermostatIcon
                    className={`${classes.iconSpacing} mr-3`}
                  />
                  {data?.leadAlertText.join(" | ")}
                </span>
              )}
              <div className="d-flex align-items-center">
                {(data?.bannerText ||
                  data?.subscriptionPausedText ||
                  data?.subscriptionRemoveText ||
                  data?.monthlyBatteryCheckData ||
                  data?.inOfficeOnlyText) && (
                  <WarningAmberOutlinedIcon className="mr-3" />
                )}
                {[
                  data?.bannerText && (
                    <span key="banner">{data.bannerText}</span>
                  ),
                  data?.inOfficeOnlyText && (
                    <span key="inOfficeOnlyText">{data?.inOfficeOnlyText}</span>
                  ),
                  data?.subscriptionPausedText && (
                    <span key="subscriptionPausedText">
                      {data.subscriptionPausedText}
                    </span>
                  ),
                  data?.subscriptionRemoveText && (
                    <span key="subscriptionRemoveText">
                      {data.subscriptionRemoveText}
                    </span>
                  ),
                  data?.monthlyBatteryCheckData && (
                    <span key="monthlyBatteryCheckData">
                      Monthly Battery Checks -{" "}
                      {formatLocalDateTime(
                        data?.monthlyBatteryCheckData
                          ?.monthlyBatteryCheckUpdatedAt
                      )}
                      {data?.monthlyBatteryCheckData?.userNameInitials}
                    </span>
                  ),
                ]
                  .filter(Boolean)
                  .reduce((acc, curr, idx, _arr) => {
                    if (idx > 0) {
                      acc.push(
                        <span
                          key={`separator-${idx}`}
                          className={classes.separator}
                        >
                          |
                        </span>
                      );
                    }
                    acc.push(curr);
                    return acc;
                  }, [])}
              </div>
            </span>
          </div>
        </AccordionDetails>
      )}
    </Accordion>
  );
};

export default AlertBannerForDeactivatePatient;
